import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Chip,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { scholarshipApi, type StudentScholarshipStatusDto } from '../../../lib/api/scholarship.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { errorText } from '../../../lib/error-text';
import { formatDate } from '../../../lib/i18n-format';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dateLabel(iso: string | null | undefined, t: TFunction): string {
  if (!iso) return t('portal.scholarship.noDate');
  try {
    return formatDate(iso, {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation('sis');
  const map: Record<
    string,
    { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }
  > = {
    draft: { label: t('portal.scholarship.status.draft'), color: 'default' },
    submitted: { label: t('portal.scholarship.status.submitted'), color: 'info' },
    reviewing: { label: t('portal.scholarship.status.reviewing'), color: 'warning' },
    approved: { label: t('portal.scholarship.status.approved'), color: 'success' },
    rejected: { label: t('portal.scholarship.status.rejected'), color: 'error' },
  };
  const cfg = map[status] ?? { label: status, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// ─── Application Status View ──────────────────────────────────────────────────

function ApplicationStatusView({ data }: { data: StudentScholarshipStatusDto }) {
  const { t } = useTranslation('sis');
  const { scholarship, application } = data;
  if (!application || !scholarship) return null;

  const steps = [
    t('portal.scholarship.stepper.submitted'),
    t('portal.scholarship.stepper.reviewing'),
    application.status === 'rejected'
      ? t('portal.scholarship.stepper.resultRejected')
      : t('portal.scholarship.stepper.resultApproved'),
  ];
  const stepIndex =
    application.status === 'submitted' ? 0 : application.status === 'reviewing' ? 1 : 2;

  return (
    <Stack spacing={3}>
      {/* Scholarship info */}
      <Paper sx={{ bgcolor: '#161b22', border: '1px solid #30363d', borderRadius: 3, p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={1}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {scholarship.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scholarship.description}
            </Typography>
          </Box>
          <StatusChip status={application.status} />
        </Stack>

        {/* Progress stepper */}
        <Box mt={3}>
          <Stepper activeStep={stepIndex} alternativeLabel>
            {steps.map((label, idx) => (
              <Step key={idx} completed={stepIndex > idx || stepIndex === 2}>
                <StepLabel
                  error={idx === 2 && application.status === 'rejected'}
                  sx={{
                    '& .MuiStepLabel-label': { color: '#8b949e', fontSize: 12 },
                    '& .MuiStepLabel-label.Mui-active': { color: '#e6edf3' },
                    '& .MuiStepLabel-label.Mui-completed': { color: '#58a6ff' },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>

      {/* Result card */}
      {application.status === 'approved' && (
        <Paper
          sx={{
            bgcolor: '#0d2018',
            border: '1px solid #238636',
            borderRadius: 3,
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CheckCircleRoundedIcon sx={{ color: '#3fb950', fontSize: 36 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#3fb950">
              {t('portal.scholarship.result.approvedTitle', {
                percent: application.approvedCoveragePercent,
              })}
            </Typography>
            <Typography variant="body2" color="#8b949e">
              {t('portal.scholarship.result.approvedOn', {
                date: dateLabel(application.reviewedAt, t),
              })}{' '}
              {application.reviewNotes &&
                t('portal.scholarship.result.committeeNote', { note: application.reviewNotes })}
            </Typography>
          </Box>
        </Paper>
      )}

      {application.status === 'rejected' && (
        <Paper
          sx={{
            bgcolor: '#2d0f12',
            border: '1px solid #6e1a1f',
            borderRadius: 3,
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CancelRoundedIcon sx={{ color: '#f85149', fontSize: 36 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#f85149">
              {t('portal.scholarship.result.rejectedTitle')}
            </Typography>
            <Typography variant="body2" color="#8b949e">
              {application.reviewNotes
                ? t('portal.scholarship.result.committeeNote', { note: application.reviewNotes })
                : t('portal.scholarship.result.rejectedBody')}
            </Typography>
          </Box>
        </Paper>
      )}

      {(application.status === 'submitted' || application.status === 'reviewing') && (
        <Paper
          sx={{
            bgcolor: '#131c2e',
            border: '1px solid #1f3a5f',
            borderRadius: 3,
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <HourglassTopRoundedIcon sx={{ color: '#58a6ff', fontSize: 36 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="#58a6ff">
              {t('portal.scholarship.result.pendingTitle')}
            </Typography>
            <Typography variant="body2" color="#8b949e">
              {t('portal.scholarship.result.pendingBody', {
                date: dateLabel(application.submittedAt, t),
              })}
            </Typography>
          </Box>
        </Paper>
      )}
    </Stack>
  );
}

// ─── Application Form ─────────────────────────────────────────────────────────

interface FormData {
  motivationStatement: string;
  financialStatement: string;
  employmentStatus: 'employed' | 'unemployed' | 'part-time';
  economicDependents: number;
  hadScholarshipBefore: boolean;
  previousScholarshipYear: string;
}

function ApplicationForm({
  scholarship,
  tenantId,
  studentId,
  onSuccess,
}: {
  scholarship: NonNullable<StudentScholarshipStatusDto['scholarship']>;
  tenantId: string;
  studentId: string | null;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('sis');
  const STEPS = [
    t('portal.scholarship.form.stepSocioeconomic'),
    t('portal.scholarship.form.stepMotivation'),
    t('portal.scholarship.form.stepReview'),
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<FormData>({
    motivationStatement: '',
    financialStatement: '',
    employmentStatus: 'unemployed',
    economicDependents: 0,
    hadScholarshipBefore: false,
    previousScholarshipYear: '',
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: () => {
      const payload: Parameters<typeof scholarshipApi.applyForScholarship>[0] = {
        tenantId,
        scholarshipId: scholarship.id,
        ...(studentId ? { personId: studentId } : {}),
        motivationStatement: form.motivationStatement,
        financialStatement: form.financialStatement,
        employmentStatus: form.employmentStatus,
        economicDependents: form.economicDependents,
        hadScholarshipBefore: form.hadScholarshipBefore,
        submit: true,
      };
      if (form.hadScholarshipBefore && form.previousScholarshipYear) {
        payload.previousScholarshipYear = form.previousScholarshipYear;
      }
      return scholarshipApi.applyForScholarship(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scholarshipStatus'] });
      onSuccess();
    },
  });

  const handleField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const canContinue = (): boolean => {
    if (activeStep === 0) {
      return form.financialStatement.trim().length >= 50;
    }
    if (activeStep === 1) {
      return form.motivationStatement.trim().length >= 100;
    }
    return true;
  };

  return (
    <Paper
      sx={{ bgcolor: '#161b22', border: '1px solid #30363d', borderRadius: 3, p: { xs: 2, sm: 4 } }}
    >
      {/* Scholarship badge */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <WorkspacePremiumRoundedIcon sx={{ color: '#f0b429', fontSize: 28 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {scholarship.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('portal.scholarship.form.coverageUpTo', {
              percent: scholarship.coveragePercent,
            })}
            {scholarship.applicationDeadline &&
              t('portal.scholarship.form.closesOn', {
                date: dateLabel(scholarship.applicationDeadline, t),
              })}
          </Typography>
        </Box>
      </Stack>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': { color: '#8b949e', fontSize: 12 },
                '& .MuiStepLabel-label.Mui-active': { color: '#e6edf3' },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Socioeconomic data */}
      {activeStep === 0 && (
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            {t('portal.scholarship.form.socioeconomicIntro')}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: '#8b949e' }}>
              {t('portal.scholarship.form.employmentStatusLabel')}
            </InputLabel>
            <Select
              value={form.employmentStatus}
              label={t('portal.scholarship.form.employmentStatusLabel')}
              onChange={(e) =>
                handleField('employmentStatus', e.target.value as FormData['employmentStatus'])
              }
              sx={{
                color: 'text.primary',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              }}
            >
              <MenuItem value="employed">
                {t('portal.scholarship.form.employmentEmployed')}
              </MenuItem>
              <MenuItem value="part-time">
                {t('portal.scholarship.form.employmentPartTime')}
              </MenuItem>
              <MenuItem value="unemployed">
                {t('portal.scholarship.form.employmentUnemployed')}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            type="number"
            size="small"
            label={t('portal.scholarship.form.dependentsLabel')}
            value={form.economicDependents}
            onChange={(e) =>
              handleField('economicDependents', Math.max(0, parseInt(e.target.value) || 0))
            }
            inputProps={{ min: 0, max: 20 }}
            helperText={t('portal.scholarship.form.dependentsHelper')}
            sx={{
              '& .MuiInputLabel-root': { color: '#8b949e' },
              '& .MuiInputBase-input': { color: 'text.primary' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '& .MuiFormHelperText-root': { color: '#8b949e' },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.hadScholarshipBefore}
                onChange={(e) => handleField('hadScholarshipBefore', e.target.checked)}
                sx={{ color: '#58a6ff' }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {t('portal.scholarship.form.hadScholarshipBefore')}
              </Typography>
            }
          />
          {form.hadScholarshipBefore && (
            <TextField
              size="small"
              label={t('portal.scholarship.form.previousScholarshipYearLabel')}
              value={form.previousScholarshipYear}
              onChange={(e) => handleField('previousScholarshipYear', e.target.value)}
              placeholder={t('portal.scholarship.form.previousScholarshipYearPlaceholder')}
              sx={{
                '& .MuiInputLabel-root': { color: '#8b949e' },
                '& .MuiInputBase-input': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              }}
            />
          )}

          <TextField
            multiline
            minRows={4}
            maxRows={8}
            label={t('portal.scholarship.form.financialStatementLabel')}
            value={form.financialStatement}
            onChange={(e) => handleField('financialStatement', e.target.value)}
            placeholder={t('portal.scholarship.form.financialStatementPlaceholder')}
            helperText={t('portal.scholarship.form.charCountMin50', {
              count: form.financialStatement.length,
            })}
            error={form.financialStatement.length > 0 && form.financialStatement.length < 50}
            sx={{
              '& .MuiInputLabel-root': { color: '#8b949e' },
              '& .MuiInputBase-input': { color: 'text.primary' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '& .MuiFormHelperText-root': { color: '#8b949e' },
            }}
          />
        </Stack>
      )}

      {/* Step 1: Motivation letter */}
      {activeStep === 1 && (
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            {t('portal.scholarship.form.motivationIntro')}
          </Typography>
          <TextField
            multiline
            minRows={8}
            maxRows={16}
            label={t('portal.scholarship.form.motivationLabel')}
            value={form.motivationStatement}
            onChange={(e) => handleField('motivationStatement', e.target.value)}
            placeholder={t('portal.scholarship.form.motivationPlaceholder')}
            helperText={t('portal.scholarship.form.charCountMin100', {
              count: form.motivationStatement.length,
            })}
            error={form.motivationStatement.length > 0 && form.motivationStatement.length < 100}
            sx={{
              '& .MuiInputLabel-root': { color: '#8b949e' },
              '& .MuiInputBase-input': { color: 'text.primary' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              '& .MuiFormHelperText-root': { color: '#8b949e' },
            }}
          />
        </Stack>
      )}

      {/* Step 2: Review & submit */}
      {activeStep === 2 && (
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {t('portal.scholarship.form.reviewIntro')}
          </Typography>
          <Divider sx={{ borderColor: '#30363d' }} />

          <Box>
            <Typography variant="caption" color="#8b949e" fontWeight={600}>
              {t('portal.scholarship.review.employmentStatus')}
            </Typography>
            <Typography variant="body2" color="text.primary" mt={0.5}>
              {
                {
                  employed: t('portal.scholarship.form.employmentEmployed'),
                  'part-time': t('portal.scholarship.form.employmentPartTime'),
                  unemployed: t('portal.scholarship.form.employmentUnemployed'),
                }[form.employmentStatus]
              }
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="#8b949e" fontWeight={600}>
              {t('portal.scholarship.review.dependents')}
            </Typography>
            <Typography variant="body2" color="text.primary" mt={0.5}>
              {form.economicDependents}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="#8b949e" fontWeight={600}>
              {t('portal.scholarship.review.financialStatement')}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              mt={0.5}
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {form.financialStatement}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="#8b949e" fontWeight={600}>
              {t('portal.scholarship.review.motivation')}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              mt={0.5}
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {form.motivationStatement}
            </Typography>
          </Box>

          {isError && (
            <Alert severity="error" sx={{ bgcolor: '#2d0f12', color: '#f85149' }}>
              {errorText(error, t('portal.scholarship.form.submitError'))}
            </Alert>
          )}
        </Stack>
      )}

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between" mt={4}>
        <Button
          variant="outlined"
          disabled={activeStep === 0 || isPending}
          onClick={() => setActiveStep((s) => s - 1)}
          sx={{ textTransform: 'none', borderColor: '#30363d', color: '#8b949e' }}
        >
          {t('portal.scholarship.nav.previous')}
        </Button>
        {activeStep < 2 ? (
          <Tooltip title={!canContinue() ? t('portal.scholarship.nav.completeFields') : ''}>
            <span>
              <Button
                variant="contained"
                disabled={!canContinue()}
                onClick={() => setActiveStep((s) => s + 1)}
                sx={{ textTransform: 'none', px: 4 }}
              >
                {t('portal.scholarship.nav.continue')}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            color="success"
            disabled={isPending}
            onClick={() => mutate()}
            startIcon={
              isPending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />
            }
            sx={{ textTransform: 'none', px: 4, fontWeight: 700 }}
          >
            {isPending
              ? t('portal.scholarship.nav.submitting')
              : t('portal.scholarship.nav.submit')}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

// ─── No Scholarship Available ──────────────────────────────────────────────────

function NoScholarshipView() {
  const { t } = useTranslation('sis');
  return (
    <Paper
      sx={{
        bgcolor: '#161b22',
        border: '1px solid #30363d',
        borderRadius: 3,
        p: { xs: 4, sm: 6 },
        textAlign: 'center',
        mt: 2,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          mx: 'auto',
          mb: 3,
          background: 'linear-gradient(135deg, #f0b429 0%, #d97706 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <WorkspacePremiumRoundedIcon sx={{ color: 'white', fontSize: 36 }} />
      </Box>
      <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
        {t('portal.scholarship.empty.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" maxWidth={420} mx="auto">
        {t('portal.scholarship.empty.body')}
      </Typography>
    </Paper>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

interface StudentScholarshipTabProps {
  termId: string | null;
  studentId: string | null;
}

export function StudentScholarshipTab({ termId, studentId }: StudentScholarshipTabProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['scholarshipStatus', currentTenantId, termId, studentId],
    queryFn: () =>
      scholarshipApi.getMyScholarshipStatus({
        tenantId: currentTenantId!,
        termId: termId!,
        ...(studentId ? { personId: studentId } : {}),
      }),
    enabled: !!currentTenantId && !!termId,
    staleTime: 30_000,
  });

  if (!termId) {
    return (
      <Paper
        sx={{
          bgcolor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 3,
          p: 4,
          mt: 2,
          textAlign: 'center',
        }}
      >
        <Typography color="text.secondary">{t('portal.scholarship.empty.noTerm')}</Typography>
      </Paper>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2, bgcolor: '#2d0f12', color: '#f85149' }}>
        {errorText(error, t('portal.scholarship.empty.loadError'))}
      </Alert>
    );
  }

  if (!data?.scholarship) return <NoScholarshipView />;

  // If there's an existing application (non-draft or submitted/reviewing/approved/rejected)
  if (data.application && data.application.status !== 'draft') {
    return <ApplicationStatusView data={data} />;
  }

  // Show success state while query revalidates
  if (submitted) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Show form to apply
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <WorkspacePremiumRoundedIcon sx={{ color: '#f0b429', fontSize: 24 }} />
        <Typography variant="h6" fontWeight={700} color="text.primary">
          {t('portal.scholarship.apply.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t('portal.scholarship.apply.body')}
      </Typography>
      <ApplicationForm
        scholarship={data.scholarship}
        tenantId={currentTenantId!}
        studentId={studentId}
        onSuccess={() => setSubmitted(true)}
      />
    </Stack>
  );
}
